import type { Table } from "@tanstack/react-table";
import { SendHorizonal, Trash2, X } from "lucide-react";
import { useTranslation } from "react-i18next";

import { formatAmount } from "@/lib/utils";
import type { GetAllInvoiceInvoice } from "@/queries/invoices/get-all";
import { useMemo } from "react";
import {
  TableActionBarButton,
  TableActionBarRoot,
  TableActionBarRowsSelected,
} from "../../../components/shared/data-table/action-bar";
import { BankTransfersStatusFilters } from "./bank-transfers-filters";
import { getPaymentAmountToPay } from "./get-payments-amount";

type Props = {
  table: Table<GetAllInvoiceInvoice>;
  onDelete: (invoiceIds: string[]) => void | Promise<void>;
  onPay: (invoiceIds: string[]) => void | Promise<void>;
  onPaid: (invoiceIds: string[]) => void | Promise<void>;
  currentTab: BankTransfersStatusFilters;
};

export const TableActionBar = ({
  table,
  onDelete,
  onPay,
  onPaid,
  currentTab,
}: Props) => {
  const { t } = useTranslation();

  const rows = table.getSelectedRowModel().rows;

  const rowIds = useMemo(() => {
    return rows.map((row) => row.id);
  }, [rows]);

  const totalAmount = useMemo(() => {
    return rows.reduce((sum, row) => {
      const paymentAmount = getPaymentAmountToPay(row.original.draftPayments);
      if (paymentAmount) {
        return sum + paymentAmount;
      }
      return sum;
    }, 0);
  }, [rows]);

  const currency = useMemo(() => {
    const firstRow = rows[0];
    return firstRow?.original?.invoice?.currency || "EUR";
  }, [rows]);

  if (rows.length === 0) return null;

  const handleDelete = () => {
    onDelete(rowIds);
  };

  const handlePay = () => {
    onPay(rowIds);
  };

  const handlePaid = () => {
    onPaid(rowIds);
  };

  return (
    <TableActionBarRoot className="gap-4">
      <TableActionBarRowsSelected table={table} />
      <div className="flex gap-2">
        <TableActionBarButton variant="outline" onClick={handleDelete}>
          <Trash2 className="w-4 h-4" />
          {t("delete")}
        </TableActionBarButton>
        {currentTab === BankTransfersStatusFilters.TO_PAY && (
          <>
            <TableActionBarButton variant="outline" onClick={handlePaid}>
              <X className="w-4 h-4" />
              {t("payables.no_transfer_needed")}
            </TableActionBarButton>
            <TableActionBarButton onClick={handlePay}>
              <SendHorizonal className="w-4 h-4" />
              {t("bank-transfers.pay")} ({formatAmount(totalAmount, currency)})
            </TableActionBarButton>
          </>
        )}
      </div>
    </TableActionBarRoot>
  );
};
